import { UploadFile } from '@mui/icons-material'
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    Link,
    Stack,
    Switch,
    Typography,
} from '@mui/material'
import config from 'config'
import React, { Fragment, useCallback, useState } from 'react'
import { ErrorCode, useDropzone } from 'react-dropzone'
import useToast from 'shared/components/Toast/useToast'
import theme from 'shared/style/theme'
import { getPxAsRem } from 'shared/utils'

import { useQuery } from '@apollo/client'
import { formatDate } from 'date-fns'
import { GetLatestErrorDownload } from 'recruiter/graphql/candidateBulkUpload'
import { baseStyle } from 'recruiter/pre-ralph/people/styled'
import useIsMobile from 'shared/hooks/useIsMobile'
import {
    BulkUploadRosterConfig,
    RosterConfigForm,
} from 'recruiter/components/RosterConfigForm'

interface UploadDocument {
    name: string
}

const BulkCandidateDocumentUpload: React.FC = () => {
    const toast = useToast()
    const isMobile = useIsMobile()
    const [uploadDocuments, setUploadDocuments] = useState<UploadDocument[]>([])
    const [uploadLoading, setUploadLoading] = useState<boolean>(false)
    const [createSearch, setCreateSearch] = useState(false)
    const [rosterConfig, setRosterConfig] =
        useState<BulkUploadRosterConfig | null>(null)
    const { data: errorDownload, refetch: refetchErrorDownload } = useQuery(
        GetLatestErrorDownload,
        {
            fetchPolicy: 'network-only',
        }
    )

    const handleDownloadClick = async (url?: string) => {
        if (!url) {
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
                severity: 'error',
            })

            return
        }

        const hiddenDownloadLink = document.createElement('a')

        hiddenDownloadLink.href = url
        hiddenDownloadLink.target = '_blank'
        hiddenDownloadLink.click()
        hiddenDownloadLink.remove()
    }

    const uploadDocument = useCallback(
        async (file: File) => {
            if (createSearch && !rosterConfig) {
                toast({
                    title: 'Search name is required',
                    message: 'Please enter a search name before uploading.',
                    severity: 'error',
                })
                return
            }

            setUploadLoading(true)
            const formData = new FormData()
            formData.append('document', file)

            if (createSearch && rosterConfig) {
                formData.append('rosterConfig', JSON.stringify(rosterConfig))
            }

            try {
                const response = await fetch(
                    `${config.api.uri}/candidateBulkCSVUpload`,
                    {
                        method: 'POST',
                        body: formData,
                        headers: {
                            contentType: 'multipart/form-data',
                            Authorization:
                                'Bearer ' + localStorage.getItem('token'),
                            boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                        },
                    }
                )
                const data = await response.json()
                if (response.ok) {
                    console.log('Document sent successfully: ', data)
                    refetchErrorDownload()
                    toast({
                        title: 'Import document successfully received',
                        message: createSearch
                            ? `${file.name} has been uploaded. A new search will be created.`
                            : `${file.name} has been uploaded`,
                        severity: 'success',
                    })
                } else {
                    console.error('Failed to receive document: ', data)
                    toast({
                        title: 'Something went wrong',
                        message: 'Please try again',
                    })
                }
            } catch (error) {
                console.error('Failed to receive document', error)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
            setUploadLoading(false)
        },
        [createSearch, rosterConfig, refetchErrorDownload, toast]
    )

    const onDrop = useCallback(
        (acceptedFiles) => {
            acceptedFiles.forEach((file: File) => {
                uploadDocument(file)
                setUploadDocuments((prev) => [...prev, { name: file.name }])
            })
        },
        [uploadDocument]
    )

    const accept = {
        'text/csv': ['.csv'],
    }
    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize: 5000000,
        onDrop,
        multiple: false,
    })

    const fileRejectionItems = fileRejections.map(({ file, errors }) => {
        const FileRejectionMessage = ({
            code,
            message,
        }: {
            code: string
            message: string
        }) => {
            switch (code) {
                case ErrorCode.FileInvalidType:
                    const acceptedTypes = Object.values(accept)
                        .flat()
                        .join(', ')
                    return <>File must be type: {acceptedTypes}</>
                case ErrorCode.FileTooLarge:
                case ErrorCode.FileTooSmall:
                case ErrorCode.TooManyFiles:
                    return <>{message}</>
            }
        }

        return (
            <Stack>
                <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    <ul>
                        {errors.map((e) => (
                            <li key={e.code}>
                                {file.name} -{' '}
                                <FileRejectionMessage
                                    code={e.code}
                                    message={e.message}
                                />
                            </li>
                        ))}
                    </ul>
                </Alert>
            </Stack>
        )
    })

    return (
        <Box sx={{ borderRadius: 2 }}>
            <FormControlLabel
                control={
                    <Switch
                        checked={createSearch}
                        onChange={(e) => setCreateSearch(e.target.checked)}
                        disabled={uploadLoading}
                    />
                }
                label="Create a new search for these candidates"
                sx={{ mt: getPxAsRem(16), mb: getPxAsRem(8) }}
            />

            {createSearch && <RosterConfigForm onChange={setRosterConfig} />}

            <Box sx={{ width: '100%' }}>
                <Button
                    sx={{
                        marginTop: getPxAsRem(48),
                    }}
                    onClick={() =>
                        handleDownloadClick(
                            process.env?.REACT_APP_BULK_UPLOAD_TEMPLATE_URL
                        )
                    }
                    variant="text"
                    color="secondary"
                >
                    Download candidate upload template
                </Button>
            </Box>
            <Box sx={baseStyle}>
                <div
                    {...getRootProps()}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <input {...getInputProps()} />
                    <IconButton size="small">
                        <UploadFile color="primary" />
                    </IconButton>
                    <Typography sx={{ px: getPxAsRem(4) }}>
                        <Link underline="hover" color="primary">
                            Click to upload
                        </Link>
                        &nbsp; or drag and drop
                    </Typography>
                    <Typography>.csv</Typography>
                </div>
            </Box>
            {fileRejectionItems.length > 0 && (
                <div style={{ marginTop: getPxAsRem(28) }}>
                    {fileRejectionItems}
                </div>
            )}
            {uploadLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: getPxAsRem(64),
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {errorDownload?.latestErrorDownload?.url && (
                <Button
                    sx={{
                        marginTop: getPxAsRem(48),
                    }}
                    onClick={() =>
                        handleDownloadClick(
                            errorDownload.latestErrorDownload!.url
                        )
                    }
                    variant="text"
                    color="secondary"
                >
                    Download Error Report From:{' '}
                    {formatDate(
                        errorDownload.latestErrorDownload.updatedAt,
                        'MM/dd/yyyy h:mm a'
                    )}
                </Button>
            )}
            {uploadDocuments.length > 0 ? (
                <Grid
                    container
                    sx={{
                        marginTop: getPxAsRem(12),
                    }}
                >
                    {uploadDocuments?.map((doc, index) => (
                        <Fragment key={`doc_${index}`}>
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    borderWidth: 1,
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    p: 2,
                                    my: 1,
                                }}
                            >
                                <Stack
                                    direction={isMobile ? 'column' : 'row'}
                                    justifyContent={
                                        isMobile ? 'center' : 'space-between'
                                    }
                                    alignItems={
                                        isMobile ? 'flex-start' : 'center'
                                    }
                                    rowGap={1}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <UploadFile
                                            color="primary"
                                            fontSize="small"
                                        />
                                        <Box sx={{ py: getPxAsRem(8) }}>
                                            <Typography>{doc.name}</Typography>
                                        </Box>
                                    </Box>
                                    <Stack
                                        direction={
                                            isMobile ? 'row-reverse' : 'row'
                                        }
                                    ></Stack>
                                </Stack>
                            </Grid>
                        </Fragment>
                    ))}
                </Grid>
            ) : (
                ''
            )}
        </Box>
    )
}

export default BulkCandidateDocumentUpload
